import { IconButton } from "@mui/material";
import styled, { css } from "styled-components";
import { Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";

const MAX_FILE_TEXT_WIDTH = "200px";
const PREVIEW_SIZE = "100px";

export const DownloadPDF = styled(IconButton)`
	width: 100%;
	color: ${({ theme }) => theme.pantoneBlack};
	padding: 10px 0;
`;

export const DownloadContainer = styled.div`
	display: flex;
	align-items: center;
	margin: 20px;
	cursor: pointer;
`;

export const ContainerPreview = styled.div<{ previewImage: string }>`
	display: flex;
	flex-direction: ${(props) => props.previewImage};
	justify-content: flex-start;
	align-items: stretch;
	flex-wrap: wrap;
	gap: 1rem;
	margin: 1rem 0 0;
`;

export const ContainerScoll = styled.div`
	flex: 1;
	min-height: 0;
	overflow: auto;
`;

export const ButtonBaseDeletePreview = styled(IconButton)`
	&&& {
		color: ${({ theme }) => theme.red};
		fill: ${({ theme }) => theme.red};

		.MuiSvgIcon-root {
			color: ${({ theme }) => theme.red};
			fill: ${({ theme }) => theme.red};
		}
	}
`;

interface IButtonDeletePreviewProps {
	$isOverCorner?: boolean;
}
export const ButtonDeletePreview = styled(ButtonBaseDeletePreview)<IButtonDeletePreviewProps>`
	&&& {
		position: absolute;
		top: ${({ $isOverCorner }) => ($isOverCorner ? "-8px" : "4px")};
		right: ${({ $isOverCorner }) => ($isOverCorner ? "-16px" : "4px")};
		padding: 2px;
		background-color: ${({ theme }) => theme.white};
		box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2);
	}
`;

export const ButtonDeleteFile = styled(ButtonBaseDeletePreview)`
	&&& {
		margin: 0 0 0 auto;
	}
`;

export const BoxImage = styled.div`
	position: relative;
	width: 100%;
	overflow: hidden;
`;

interface IPreviewFrameProps {
	$isImage: boolean;
}
export const PreviewFrame = styled.div<IPreviewFrameProps>`
	display: flex;
	align-items: flex-end;
	justify-content: center;
	width: ${PREVIEW_SIZE};
	margin: 0 auto;

	${({ $isImage }) =>
		$isImage
			? css`
					height: ${PREVIEW_SIZE};
				`
			: css`
					padding-top: 8px;
				`}
`;

interface IPreviewContentProps {
	$isClickable: boolean;
}
export const PreviewContent = styled.div<IPreviewContentProps>`
	position: relative;
	display: inline-flex;
	cursor: ${({ $isClickable }) => ($isClickable ? "zoom-in" : "default")};

	img {
		display: block;
		width: auto;
		height: auto;
		max-width: ${PREVIEW_SIZE};
		max-height: ${PREVIEW_SIZE};
		object-fit: contain;
		cursor: inherit;
	}
`;
export const ItemFile = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;
	gap: 0.6rem;
	background-color: ${({ theme }) => theme.white};
	box-shadow: 2px 2px 2px 0 rgba(0, 0, 0, 0.2);
	border-radius: 5px;
	padding: 1rem;
	width: 100%;
	max-width: 600px;

	> p {
		display: flex;
		flex-wrap: nowrap;
		flex-direction: row;
		margin: 0;
		max-width: 265px;

		> span {
			max-width: 280px;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: pre;
		}
	}

	@media (max-width: 560px) {
		> p {
			max-width: 70%;
		}
	}
	@media (max-width: 406px) {
		> p {
			max-width: 53%;
		}
	}
`;

export const ImagePreview = styled.img`
	width: 100%;
	max-width: 100px;
	height: 100px;
`;

export const AreaIcons = styled.div`
	margin-left: auto;
`;

export const PaginationLabel = styled.div`
	align: left;
`;

interface IAreaFilesProps {
	$hasDataGridImages: boolean;
}
export const AreaFiles = styled.div<IAreaFilesProps>`
	overflow: auto;

	${(props) =>
		props.$hasDataGridImages
			? css`
					max-height: 300px;
				`
			: css`
					flex: 1;
					height: 100%;
				`}
`;

export const NameFile = styled.div`
	width: 100%;
	max-width: 200px;
	overflow: hidden;
	text-overflow: ellipsis;
`;

export const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	height: 76vh;
`;

export const useStyles = makeStyles<Theme>((theme) => ({
	buttonDownloadPreview: {
		"&": {
			position: "absolute",
			bottom: "8px",
			right: "8px",
			backgroundColor: "white",
			borderRadius: "50%",
			width: "28px",
			height: "28px",
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			cursor: "pointer",
			boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
			color: theme.palette.primary.main,
		},
	},
	buttonBaseDeletePreview: {
		"&": {
			color: theme.palette.primary.main,
			fill: theme.palette.primary.main,
		},
		"& .MuiSvgIcon-root": {
			color: theme.palette.primary.main,
			fill: theme.palette.primary.main,
		},
	},
}));

export const BoxModalImages = styled.div`
	max-height: 100%;
	flex: 1;
	overflow-x: hidden;
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
`;

export const LabelFileName = styled.span`
	display: flex;
	width: 100%;
	max-width: ${MAX_FILE_TEXT_WIDTH};
	font-size: 12px;
	white-space: nowrap;
`;

export const FileNameBase = styled.span`
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
`;

export const FileNameExtension = styled.span`
	flex-shrink: 0;
`;

export const FileInfo = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
	max-width: ${PREVIEW_SIZE};
	min-width: 0;
	margin: 0.25rem auto 0;
	overflow: hidden;

	> div {
		max-width: 100%;
	}
`;

export const FileNameRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	min-height: 28px;
`;

export const FileNameArea = styled.div`
	min-width: 0;
	overflow: hidden;
	text-align: center;

	> div {
		max-width: 100%;
		margin: 0 auto;
	}
`;

export const DownloadArea = styled.div`
	flex-shrink: 0;

	.MuiIconButton-root {
		padding: 4px;
	}
`;

export const EvidenceSectionTitle = styled.span`
	display: block;
	margin: 1rem 0 0.5rem;
	font-size: 11px;
	font-weight: bold;
	text-transform: uppercase;
	color: ${({ theme }) => theme.pantoneBlack};
`;

export const EvidenceQueueItem = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	padding: 0.75rem;
	margin-bottom: 0.5rem;
	border-radius: 5px;
	background-color: ${({ theme }) => theme.white};
	box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2);
`;

export const EvidenceQueueItemHeader = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 0.6rem;
`;

export const EvidenceThumbnail = styled.img`
	width: 40px;
	height: 40px;
	flex-shrink: 0;
	object-fit: contain;
	border-radius: 4px;
`;

export const EvidenceQueueItemInfo = styled.div`
	display: flex;
	flex-direction: column;
	overflow: hidden;
`;

export const EvidenceFileSize = styled.span`
	font-size: 11px;
	opacity: 0.7;
`;

export const EvidenceFooter = styled.div`
	display: flex;
	justify-content: center;
	gap: 0.5rem;
	margin-top: 0.5rem;
`;

export const EvidenceUploaderContainer = styled.div`
	border: 1px solid ${({ theme }) => theme.pantone2726C};
	border-radius: 5px;
	padding: 0.75rem;
	margin-bottom: 0.5rem;
`;

export const FileCommentText = styled.span`
	display: block;
	width: 100%;
	max-width: ${MAX_FILE_TEXT_WIDTH};
	margin: 0 auto;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-size: 11px;
	line-height: 1.3;
	text-align: center;
	opacity: 0.7;
`;

export const EvidenceGroupContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	padding: 0.75rem 0.5rem;
`;

export const PartSectionHeader = styled.span`
	font-weight: bold;
	color: ${({ theme }) => theme.pantoneBlack};
`;

export const PartGroupsContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	padding: 0.5rem;
`;

export const PartAttachmentsScroll = styled.div`
	max-height: 300px;
	overflow-y: auto;
`;

export const PartAttachmentsHeader = styled.span`
	display: flex;
	align-items: center;
	gap: 0.25rem;
`;
